import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  getAllBranch,
  getAllRoutes,
  getAllRoutesByCompany,
  getChannels,
  getCustomerGroups,
} from "../../services";
import { Branch } from "../../types/user.type";
import { SaleRoute } from "../../types/sale-route.type";
import { CustomerChannel, CustomerGroup } from "../../types/customer.type";
import DateRangePicker, { defaultPresetGroups } from "../common/DateRangePicker";
import { PIVOT_MAX_SPAN_DAYS, usePivotBranchLocked } from "./pivotLimits";
import { PV } from "./pivotTokens";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";

/**
 * The pivot endpoints reject a span wider than `PIVOT_MAX_SPAN_DAYS` (`date_to − date_from`),
 * so that cap is what "ทั้งหมดที่มี" resolves to here — anything wider would 400. The
 * preset subtracts the lookback from today, producing exactly that difference, so the cap
 * can be used as-is. Other pages must pass their own lookback rather than inherit this one.
 */
const PIVOT_PRESET_GROUPS = defaultPresetGroups({
  allTimeLookbackDays: PIVOT_MAX_SPAN_DAYS,
});

export interface PivotFilterState {
  dateFrom: Dayjs;
  dateTo: Dayjs;
  /** "all" or a branch_code */
  branchCode: string;
  /** "all" or a route_code */
  routeCode: string;
  /** channel codes (`CH######`) */
  customerTypes: string[];
  /**
   * customer-group codes (`CG######`).
   *
   * Separate from `customerTypes` because they are different axes: the backend resolves
   * `customer_type_code` from the channel and `customer_group_code` from the group, and a
   * store carries both. Selecting in one does not narrow the other's options.
   */
  customerGroups: string[];
  q: string;
}

interface PivotFilterBarProps {
  filters: PivotFilterState;
  onChange: (next: PivotFilterState) => void;
}

/** Compact select styled like the mockup's `.sel` — 13.5px, 8px radius, hairline border. */
const selectSx = {
  height: 38,
  minWidth: 150,
  fontSize: 13.5,
  bgcolor: PV.card,
  borderRadius: PV.radiusSm,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: PV.line },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: PV.indigoBorder },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PV.indigo },
  "& .MuiSelect-select": { py: 0, display: "flex", alignItems: "center" },
} as const;

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography sx={{ fontSize: 13, color: PV.tx2, whiteSpace: "nowrap" }}>
      {label}
    </Typography>
    {children}
  </Box>
);

export default function PivotFilterBar({ filters, onChange }: PivotFilterBarProps) {
  // Who may actually choose a branch, per the backend's own rule — not per the roles that
  // merely reach this page. See `usePivotBranchLocked`.
  const isHqLike = !usePivotBranchLocked();

  // The picker's own floor, so a hand-picked range cannot exceed the API's span cap the way
  // the "ทั้งหมดที่มี" preset no longer can. `maxDate` already defaults to today, so every
  // selectable pair is within the cap by construction.
  const minDate = useMemo(() => dayjs().subtract(PIVOT_MAX_SPAN_DAYS, "day"), []);

  const [branches, setBranches] = useState<Branch[]>([]);
  const [routes, setRoutes] = useState<SaleRoute[]>([]);
  const [channels, setChannels] = useState<CustomerChannel[]>([]);
  const [groups, setGroups] = useState<CustomerGroup[]>([]);

  const set = (patch: Partial<PivotFilterState>) => onChange({ ...filters, ...patch });

  // Every fetch here swallows its rejection: the shared axios interceptor already reports the
  // failure, and an unhandled one would escape as a runtime error overlay. An empty dropdown
  // degrades to "all", which still loads the page.
  useEffect(() => {
    if (!isHqLike) return;
    (async () => {
      try {
        const {
          data: { data },
        } = await getAllBranch();
        setBranches(Array.isArray(data) ? data : (data?.rows ?? []));
      } catch {
        setBranches([]);
      }
    })();
  }, [isHqLike]);

  const selectedBranch = branches.find((b) => b.branch_code === filters.branchCode);

  const loadRoutes = useCallback(async () => {
    // HO is not pinned to a branch, so /routes/by-company would return head office's own
    // routes. Send the SELECTED branch's company_id instead — same pattern as Sales/Filter.
    try {
      if (isHqLike) {
        if (!selectedBranch) {
          setRoutes([]);
          return;
        }
        const {
          data: { data },
        } = await getAllRoutes(selectedBranch.company_id);
        setRoutes(Array.isArray(data) ? data : (data?.rows ?? []));
      } else {
        const {
          data: { data },
        } = await getAllRoutesByCompany();
        setRoutes(Array.isArray(data) ? data : (data?.rows ?? []));
      }
    } catch {
      setRoutes([]);
    }
  }, [isHqLike, selectedBranch]);

  useEffect(() => {
    loadRoutes();
  }, [loadRoutes]);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { data },
        } = await getChannels();
        setChannels(Array.isArray(data) ? data : (data?.rows ?? []));
      } catch {
        setChannels([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { data },
        } = await getCustomerGroups();
        setGroups(Array.isArray(data) ? data : (data?.rows ?? []));
      } catch {
        setGroups([]);
      }
    })();
  }, []);

  const isRouteDisabled = isHqLike && filters.branchCode === "all";
  const typeCount = filters.customerTypes.length;
  const groupCount = filters.customerGroups.length;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, flexWrap: "wrap" }}>
      <Box sx={{ minWidth: 280 }}>
        <DateRangePicker
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          onChange={(from, to) => set({ dateFrom: from, dateTo: to })}
          minDate={minDate}
          presetGroups={PIVOT_PRESET_GROUPS}
        />
      </Box>

      {isHqLike && (
        <Field label="สาขา">
          <Select
            size="small"
            value={filters.branchCode}
            onChange={(e) => set({ branchCode: e.target.value, routeCode: "all" })}
            sx={selectSx}
          >
            <MenuItem value="all">สาขาทั้งหมด</MenuItem>
            {branches.map((b) => (
              <MenuItem key={b.uuid} value={b.branch_code}>
                {b.branch_code} - {b.branch_name}
              </MenuItem>
            ))}
          </Select>
        </Field>
      )}

      <Field label="สายส่ง">
        <Select
          size="small"
          disabled={isRouteDisabled}
          value={filters.routeCode}
          onChange={(e) => set({ routeCode: e.target.value })}
          sx={selectSx}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {routes.map((r) => (
            <MenuItem key={r.route_code} value={r.route_code}>
              {r.route_code} - {r.route_name}
            </MenuItem>
          ))}
        </Select>
      </Field>

      <Field label="ประเภทลูกค้า">
        <Select
          size="small"
          multiple
          displayEmpty
          value={filters.customerTypes}
          onChange={(e) =>
            set({
              customerTypes:
                typeof e.target.value === "string"
                  ? e.target.value.split(",")
                  : e.target.value,
            })
          }
          renderValue={() =>
            typeCount === 0 ? "รวมทั้งหมด" : `เลือกไว้ ${typeCount} ประเภท`
          }
          sx={selectSx}
        >
          {channels.map((c) => (
            <MenuItem key={c.uuid} value={c.code} sx={{ py: 0.25 }}>
              <Checkbox size="small" checked={filters.customerTypes.includes(c.code)} />
              <ListItemText
                primary={c.name}
                secondary={c.code}
                slotProps={{
                  primary: { fontSize: 13.5 },
                  secondary: { fontSize: 11.5 },
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </Field>

      <Field label="กลุ่มลูกค้า">
        <Select
          size="small"
          multiple
          displayEmpty
          value={filters.customerGroups}
          onChange={(e) =>
            set({
              customerGroups:
                typeof e.target.value === "string"
                  ? e.target.value.split(",")
                  : e.target.value,
            })
          }
          renderValue={() =>
            groupCount === 0 ? "รวมทั้งหมด" : `เลือกไว้ ${groupCount} กลุ่ม`
          }
          sx={selectSx}
        >
          {groups.map((g) => (
            <MenuItem key={g.uuid} value={g.code} sx={{ py: 0.25 }}>
              <Checkbox size="small" checked={filters.customerGroups.includes(g.code)} />
              <ListItemText
                primary={g.name}
                secondary={g.code}
                slotProps={{
                  primary: { fontSize: 13.5 },
                  secondary: { fontSize: 11.5 },
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </Field>
    </Box>
  );
}
